import React, { useState, useEffect, useContext, useRef } from "react";
import { Container, Row, Col, Form } from "reactstrap";
import {
  IAddUpdateNewsfeedPageResponse,
  IAddUpdateNewsfeedProps,
  ISelectedBrand,
} from "../../../interfaces/Index";
import HeadingText from "../../common/HeadingText/HeadingText";
import LoaderComponent from "../../common/Loader/Loader";
import { AbilityContext, Can } from "../../../config/Can";
import FormSectionLayout from "components/layouts/FormSectionLayout";
import SearchableSelect from "components/common/SearchableSelect/SearchableSelect";
import { NEWSFEED_CONTENT_TYPE } from "config/constants";
import TextField from "components/common/TextField/TextField";
import Switch from "components/common/Switch/Switch";
import ButtonComponent from "components/common/Button/ButtonComponent";
import {
  normalizeCreateNewsfeedPayload,
  normalizeUpdateNewsfeedPayload,
} from "normalizer/NewsfeedNormalizer";
import backarrow from "../../../assets/images/back_arrow.svg";

const AddUpdateNewsfeedPage: React.FunctionComponent<
  IAddUpdateNewsfeedProps
> = (props: IAddUpdateNewsfeedProps) => {
  const ability = useContext(AbilityContext);

  const [newsfeedState, setNewsfeedState] =
    useState<IAddUpdateNewsfeedPageResponse>({
      id: props.selectedNewsfeedItem ? props.selectedNewsfeedItem.id : "",
      title: props.selectedNewsfeedItem ? props.selectedNewsfeedItem.title : "",
      description: props.selectedNewsfeedItem
        ? props.selectedNewsfeedItem.description
        : "",
      content: props.selectedNewsfeedItem
        ? props.selectedNewsfeedItem.content || [{ id: "", url: "", type: "" }]
        : [],
      contentId: props.selectedNewsfeedItem
        ? props.selectedNewsfeedItem.content[0].id || ""
        : "",
      contentType: props.selectedNewsfeedItem
        ? props.selectedNewsfeedItem.content[0].type || ""
        : { id: "Select", value: "Select", label: "Select" },
      contentUrl: props.selectedNewsfeedItem
        ? props.selectedNewsfeedItem.content[0].url
        : "",
      cta: props.selectedNewsfeedItem
        ? props.selectedNewsfeedItem.cta || [{ id: "", name: "", action: "" }]
        : [],
      ctaId: props.selectedNewsfeedItem
        ? props.selectedNewsfeedItem.cta.length
          ? props.selectedNewsfeedItem.cta[0].id
          : null || ""
        : "",
      ctaName: props.selectedNewsfeedItem
        ? props.selectedNewsfeedItem.cta.length
          ? props.selectedNewsfeedItem.cta[0].name
          : null || ""
        : "",
      ctaAction: props.selectedNewsfeedItem
        ? props.selectedNewsfeedItem.cta.length
          ? props.selectedNewsfeedItem.cta[0].action
          : null || ""
        : "",
      status: props.selectedNewsfeedItem
        ? props.selectedNewsfeedItem.enabled === "ACTIVE"
          ? true
          : false
        : false,
    });

  const { id, title, contentType, contentUrl, ctaName, ctaAction, status } =
    newsfeedState;

  useEffect(() => {
    const { isNewsfeedAdded, backButtonHandler, isNewsfeedUpdated } = props;
    if (isNewsfeedAdded) {
      backButtonHandler(true);
    } else if (isNewsfeedUpdated) {
      backButtonHandler(true);
    }
  }, [props.isNewsfeedAdded, props.isNewsfeedUpdated]);

  useEffect(() => {
    renderContentType();
  }, [props.selectedNewsfeedItem, contentType]);

  const renderContentType = () => {
    NEWSFEED_CONTENT_TYPE.map((item: ISelectedBrand) => {
      if (contentType === item.label) {
        setNewsfeedState({
          ...newsfeedState,
          contentType: item,
        });
      }
    });
  };

  const {
    isEdit,
    backButtonHandler,
    createNewsfeed,
    updateNewsfeed,
    // clearNewsfeedDetails,
    isLoading,
  } = props;

  const handleGoBackButton = () => {
    // isEdit && clearNewsfeedDetails();
    backButtonHandler(false);
  };

  const handleSearchableSelect = (e: any, fieldId: string) => {
    setNewsfeedState({
      ...newsfeedState,
      [fieldId]: e,
    });
  };

  const handleChange = (e: any) => {
    const fieldId: string = e.target.id;
    const fieldValue: string = e.target.value;
    setNewsfeedState({
      ...newsfeedState,
      [fieldId]: fieldValue,
      [fieldId]: fieldId === "status" ? !newsfeedState[fieldId] : fieldValue,
    });
  };

  const handleSubmit = (e: any) => {
    if (isEdit) {
      const reqPayload = normalizeUpdateNewsfeedPayload(newsfeedState);
      updateNewsfeed(id, reqPayload);
    } else {
      const reqPayload = normalizeCreateNewsfeedPayload(newsfeedState);
      createNewsfeed(reqPayload);
    }
  };

  const renderContent = () => {
    return (
      <FormSectionLayout subTitleText="Content">
        <SearchableSelect
          id="contentType"
          isSearchable={true}
          options={NEWSFEED_CONTENT_TYPE}
          onChange={(e: any) => {
            handleSearchableSelect(e, "contentType");
          }}
          label="Content type"
          placeholder="Select content type"
          selectedValue={contentType}
        />
        <TextField
          id="title"
          label="Content Title"
          placeholder="e.g. Paiya ghumaein aur jeetain coinsss"
          value={title}
          handleChange={handleChange}
        />
        {(contentType == "" ||
          contentType.value == "IMAGE" ||
          contentType.value == "VIDEO") && (
          <TextField
            id="contentUrl"
            label="Content Link"
            placeholder="e.g. link"
            value={contentUrl}
            handleChange={handleChange}
          />
        )}
        <TextField
          id="ctaName"
          label="CTA Name"
          placeholder="e.g. Deeplink/ no deeplink"
          value={ctaName}
          handleChange={handleChange}
        />
        <TextField
          id="ctaAction"
          label="CTA Link"
          placeholder="e.g. Deeplink/ no deeplink"
          value={ctaAction}
          handleChange={handleChange}
        />
      </FormSectionLayout>
    );
  };

  const renderPositionVisibility = () => {
    return (
      <FormSectionLayout subTitleText="Position and visibility">
        <Switch
          id="status"
          label="Status"
          checked={status as boolean}
          handleSwitch={handleChange}
          statusVal1={"ACTIVE"}
          statusVal2={"INACTIVE"}
        />
        {renderFormButton()}
      </FormSectionLayout>
    );
  };

  const renderFormButton = () => {
    return (
      <div className="form-row-separator">
        <Row id="addEditButtonRow">
          <Col lg="6">
            {ability.can("update", "post") && (
              <ButtonComponent
                buttonClass="large-button btn-primary submit-button"
                buttonText={isEdit ? "Update newsfeed" : "Add Newsfeed"}
                onClickHandler={handleSubmit}
              />
            )}
          </Col>
        </Row>
      </div>
    );
  };

  return (
    <div>
      <Container fluid>
        {isLoading && <LoaderComponent />}
        <Row>
          <Col>
            <div className="back-button">
              <a href="javascript:void(0)" onClick={handleGoBackButton}>
                {" "}
                {/* TO-DO: Convert this to a button in all compoents. */}
                <span>
                  <img src={backarrow} alt="close" />
                </span>{" "}
                Back to Newsfeed list
              </a>
            </div>
            <HeadingText text="Newsfeed" isMainHeading={true} />
          </Col>
        </Row>
        <Row className="form-container add-update-newsfeed-config-page">
          <Col xs="12">
            <Form>
              <div className="form-row-separator">
                {renderContent()}
                {renderPositionVisibility()}
              </div>
            </Form>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default AddUpdateNewsfeedPage;
